import {
  Card,
  Flexbox,
  Grid,
  Heading,
  Icon,
  Spacer,
  Text,
  ToggleSwitch,
  toast,
} from '@components/common'
import UserTable from './UserTable'
import {
  useGetOrganizationQuery,
  useToggleUse2FAMutation,
} from '@api/admPortal/organizationAPI'
import OrganizationTable from './OrganizationTable'
import OrganizationNameLoading from './OrganizationTable/OrganizationNameLoading'
import { useSelector } from 'react-redux'

const FixedAssetsAdmin = () => {
  const {
    data: organizationData,
    isFetching: isFetchingOrgData,
  } = useGetOrganizationQuery()

  const assetCompanyID = useSelector<any>(state => state.company) as number
  const [toggleUse2FA, { isLoading: isTogglingUse2FA }] = useToggleUse2FAMutation()

  const handleToggleUse2FA = async () => {
    try {
      await toggleUse2FA({ assetCompanyID }).unwrap()
      toast.success('2FA authentication setting updated successfully')
    } catch (error) {
      toast.error('Failed to update 2FA authentication setting')
    }
  }

  return (
    <>
      <Card>
        <Flexbox flexDirection="row" gap={6} alignItems="center">
          <Icon name="Briefcase" size="m" color="var(--primary-color)" />
          <Heading type="h2" primary>
            Organization Profile
          </Heading>
        </Flexbox>

        <Spacer y="default" />

        <Card>
          <Grid>
            <Grid.Item m={6}>
              <Flexbox flexDirection="column" gap={6} alignItems="flex-start">
                <Heading
                  type="h4"
                  color="var(--primary-color)"
                  style={{ color: 'var(--text-light)' }}
                >
                  Organization Name
                </Heading>
                {isFetchingOrgData ? (
                  <OrganizationNameLoading />
                ) : (
                  <Heading type="h1">{organizationData.name}</Heading>
                )}
              </Flexbox>
            </Grid.Item>

            <Grid.Item m={6}>
              <Flexbox flexDirection="row" gap={6} alignItems="flex-start">
                <Icon name="Calendar" size="m" color="var(--text-light)" />
                <Heading type="h4" style={{ color: 'var(--text-light)' }}>
                  Created
                </Heading>
              </Flexbox>
              <Text>{organizationData?.created ?? ''}</Text>
            </Grid.Item>

            <Grid.Item m={6}>
              <Flexbox flexDirection="column" gap={6} alignItems="flex-start">
                <Heading
                  type="h4"
                  color="var(--primary-color)"
                  style={{ color: 'var(--text-light)' }}
                >
                  Status
                </Heading>

                <Flexbox
                  flexDirection="row"
                  gap={6}
                  alignItems="center"
                  style={{
                    backgroundColor:
                      organizationData?.status === 'Active'
                        ? 'var(--primary-color50)'
                        : 'var(--danger-color50)',
                    padding: '5px 15px',
                    borderRadius: 'var(--pill-radius)',
                  }}
                >
                  <Icon
                    name={organizationData?.status === 'Active' ? 'CheckCircle' : 'XCircle'}
                    size="s"
                    color={
                      organizationData?.status === 'Active'
                        ? 'var(--primary-color)'
                        : 'var(--danger-color)'
                    }
                  />
                  <Text
                    style={{
                      color:
                        organizationData?.status === 'Active'
                          ? 'var(--primary-color)'
                          : 'var(--danger-color)',
                    }}
                  >
                    {organizationData?.status ?? ''}
                  </Text>
                </Flexbox>
              </Flexbox>
            </Grid.Item>

            <Grid.Item m={6}>
              <Flexbox flexDirection="column" gap={6} alignItems="flex-start">
                <Flexbox flexDirection="row" gap={6} alignItems="flex-start">
                  <Icon name="MapPin" size="m" color="var(--text-light)" />
                  <Heading
                    type="h4"
                    color="var(--primary-color)"
                    style={{ color: 'var(--text-light)' }}
                  >
                    Address
                  </Heading>
                </Flexbox>
              </Flexbox>
              <Text>{organizationData?.address ?? ''}</Text>
            </Grid.Item>

            <Grid.Item m={6}>
              <Flexbox flexDirection="row" gap={6} alignItems="center">
                <Icon name="Shield" size="m" color="var(--text-light)" />
                <Heading
                  type="h4"
                  color="var(--primary-color)"
                  style={{ color: 'var(--text-light)' }}
                >
                  2FA Authentication
                </Heading>
                <ToggleSwitch
                  id="use2FA"
                  name="use2FA"
                  checked={organizationData?.use2FA ?? false}
                  onChange={handleToggleUse2FA}
                  disabled={isTogglingUse2FA || isFetchingOrgData}
                  noBorder={true}
                />
              </Flexbox>
            </Grid.Item>
          </Grid>
        </Card>

        <Spacer y="large" />

        <OrganizationTable
          organizationData={organizationData}
          isFetchingOrgData={isFetchingOrgData}
        />
      </Card>

      <Spacer y="default" />

      <Card>
        <UserTable />
      </Card>
    </>
  )
}

export default FixedAssetsAdmin
